import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { Reducer } from 'redux';
import { PersistPartial } from 'redux-persist/es/persistReducer';
import { put, takeLatest, call } from 'redux-saga/effects';

import { ActionsUnion, createAction } from '../../utils/action-helper';
import {
  getTariffs,
  editTariff,
} from '../../crud/tariff.crud';
import { IServerResponse } from '../../interfaces/server';
import { ITariff, ITariffEditProps } from '../../interfaces/tariff';
import { TAppActions } from '../rootDuck';

const FETCH_REQUEST = 'tariffs/FETCH_REQUEST';
const FETCH_SUCCESS = 'tariffs/FETCH_SUCCESS';
const FETCH_FAIL = 'tariffs/FETCH_FAIL';

const CLEAR_EDIT = 'tariffs/CLEAR_EDIT';
const EDIT_REQUEST = 'tariffs/EDIT_REQUEST';
const EDIT_SUCCESS = 'tariffs/EDIT_SUCCESS';
const EDIT_FAIL = 'tariffs/EDIT_FAIL';

export type TariffEditFields =
  | 'price7days'
  | 'price30days'
  | 'price120days'
  | 'price365days'
  | 'displaying_other_contacts';

export interface IInitialState {
  tariffs: ITariff[];
  loading: boolean;
  success: boolean;
  error: string | null;

  payTariff: ITariff | undefined;
  byIdLoading: boolean;
  byIdSuccess: boolean;
  byIdError: string | null;

  editLoading: boolean;
  editSuccess: boolean;
  editError: string | null;

  delLoading: boolean;
  delSuccess: boolean;
  delError: string | null;
}

const initialState: IInitialState = {
  tariffs: [],
  loading: false,
  success: false,
  error: null,

  payTariff: undefined,
  byIdLoading: false,
  byIdSuccess: false,
  byIdError: null,

  editLoading: false,
  editSuccess: false,
  editError: null,

  delLoading: false,
  delSuccess: false,
  delError: null,
};

export const reducer: Reducer<IInitialState & PersistPartial, TAppActions> = persistReducer(
  { storage, key: 'tariffs', whitelist: ['user', 'authToken'] },
  (state = initialState, action) => {
    switch (action.type) {
      case FETCH_REQUEST: {
        return {
          ...state,
          tariffs: [],
          loading: true,
          success: false,
          error: null,
        };
      }

      case FETCH_SUCCESS: {
        return {
          ...state,
          total: action.payload.total,
          tariffs: action.payload.data,
          loading: false,
          success: true,
        };
      }

      case FETCH_FAIL: {
        return { ...state, loading: false, error: action.payload };
      }

      case CLEAR_EDIT: {
        return {
          ...state,
          tariff: undefined,
          editLoading: false,
          editSuccess: false,
          editError: null,
          delError: null,
        };
      }

      case EDIT_REQUEST: {
        return { ...state, editLoading: true, editSuccess: false, editError: null };
      }

      case EDIT_SUCCESS: {
        return {
          ...state,
          editLoading: false,
          editSuccess: true,
          tariff: action.payload.data,
        };
      }

      case EDIT_FAIL: {
        return { ...state, editLoading: false, editError: action.payload };
      }

      default:
        return state;
    }
  }
);

export const actions = {
  fetchRequest: () => createAction(FETCH_REQUEST),
  fetchSuccess: (payload: IServerResponse<ITariff[]>) => createAction(FETCH_SUCCESS, payload),
  fetchFail: (payload: string) => createAction(FETCH_FAIL, payload),

  clearEdit: () => createAction(CLEAR_EDIT),
  editRequest: (payload: { id: number; data: ITariffEditProps | any }) =>
    createAction(EDIT_REQUEST, payload),
  editSuccess: (payload: IServerResponse<ITariff>) => createAction(EDIT_SUCCESS, payload),
  editFail: (payload: string) => createAction(EDIT_FAIL, payload),
};

export type TActions = ActionsUnion<typeof actions>;

function* fetchSaga() {
  try {
    const { data }: { data: IServerResponse<ITariff[]> } = yield call(() => getTariffs());
    yield put(actions.fetchSuccess(data));
  } catch (e) {
    yield put(actions.fetchFail(e?.response?.data?.message || 'Network error'));
  }
}

function* editSaga({ payload }: { payload: { id: number; data: ITariffEditProps } }) {
  try {
    const { data }: { data: IServerResponse<ITariff> } = yield call(() =>
      editTariff(payload.id, payload.data)
    );
    yield put(actions.editSuccess(data));
  } catch (e) {
    yield put(actions.editFail(e?.response?.data?.message || 'Network error'));
  }
}

export function* saga() {
  yield takeLatest<ReturnType<typeof actions.fetchRequest>>(FETCH_REQUEST, fetchSaga);
  yield takeLatest<ReturnType<typeof actions.editRequest>>(EDIT_REQUEST, editSaga);
}
